/**
 * Conversions — partner leads & sales.
 *
 * "Refined dark command-center" treatment: atmospheric glow, orchestrated
 * staggered load, premium stat tiles, a polished status-coded table and a rich
 * empty state. Filters: status · type · period · offer search.
 */
import { useState, type CSSProperties } from 'react';
import {
  ArrowRight,
  Bitcoin,
  ChevronLeft,
  ChevronRight,
  CircleCheck,
  Clock,
  Coins,
  FilterX,
  Search,
  Sparkles,
  TrendingUp,
  Wallet,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  Input,
  PageHeader,
  Segmented,
  Select,
  Skeleton,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import { partnerApi } from '@/lib/api-affiliate';
import { formatCents, formatDate, formatNumber } from '@/lib/format';
import type { Conversion, ConversionStatus, ConversionType, Period } from '@/lib/types';

// ─────────────────────────────────────────────────────────────────────────
//  Meta
// ─────────────────────────────────────────────────────────────────────────

const rise = (ms: number): CSSProperties => ({
  animation: 'cvRise 0.55s var(--ease-out) both',
  animationDelay: `${ms}ms`,
});
const fade = (ms: number): CSSProperties => ({
  animation: 'cvFade 0.5s var(--ease-out) both',
  animationDelay: `${ms}ms`,
});

const TYPE_META: Record<ConversionType, { label: string; tone: 'info' | 'success' | 'accent' }> = {
  lead: { label: 'Лид', tone: 'info' },
  trial: { label: 'Триал', tone: 'info' },
  subscription: { label: 'Подписка', tone: 'accent' },
  purchase: { label: 'Покупка', tone: 'success' },
  recurring: { label: 'Рекуррент', tone: 'accent' },
};

const STATUS_META: Record<
  ConversionStatus,
  { label: string; tone: 'success' | 'warning' | 'danger' | 'neutral'; dot?: boolean }
> = {
  paid: { label: 'Выплачена', tone: 'success' },
  pending: { label: 'Ожидает', tone: 'warning', dot: true },
  declined: { label: 'Отклонена', tone: 'danger' },
  refunded: { label: 'Возврат', tone: 'neutral' },
};

function offerIcon(slug: string): LucideIcon {
  return /crypto|крипт/i.test(slug) ? Bitcoin : Sparkles;
}

// ─────────────────────────────────────────────────────────────────────────
//  Small pieces
// ─────────────────────────────────────────────────────────────────────────

function StatTile({
  icon: Icon,
  label,
  value,
  tint,
  style,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  tint: string;
  style?: CSSProperties;
}) {
  return (
    <div
      className="group relative overflow-hidden rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--bg-raised)] p-4 transition-[border-color,transform] duration-[var(--duration-base)] ease-[var(--ease-out)] hover:-translate-y-0.5 hover:border-[var(--border-strong)]"
      style={style}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full opacity-50 transition-opacity duration-[var(--duration-base)] group-hover:opacity-90"
        style={{ background: `radial-gradient(circle, color-mix(in oklch, ${tint} 34%, transparent), transparent 70%)` }}
      />
      <div className="relative flex items-center gap-3">
        <span
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[var(--radius-md)]"
          style={{
            backgroundColor: `color-mix(in oklch, ${tint} 15%, transparent)`,
            border: `1px solid color-mix(in oklch, ${tint} 30%, transparent)`,
            color: tint,
          }}
          aria-hidden
        >
          <Icon size={18} strokeWidth={2} />
        </span>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[var(--tracking-eyebrow)] font-semibold text-[var(--text-muted)]">
            {label}
          </div>
          <div
            className="mt-0.5 text-[22px] font-bold tabular-nums leading-none text-[var(--text)]"
            style={{ letterSpacing: 'var(--tracking-tight)' }}
          >
            {value}
          </div>
        </div>
      </div>
    </div>
  );
}

function CountryTag({ code }: { code?: string | null }) {
  if (!code) return <span className="text-[var(--text-quiet)]">—</span>;
  return (
    <span className="inline-flex h-[22px] items-center rounded-[var(--radius-xs)] border border-[var(--border)] bg-[var(--bg-base)]/60 px-2 align-middle text-[10px] font-bold tracking-[0.06em] text-[var(--text-secondary)]">
      {code.toUpperCase()}
    </span>
  );
}

function Pagination({
  page,
  pages,
  total,
  onChange,
}: {
  page: number;
  pages: number;
  total: number;
  onChange: (p: number) => void;
}) {
  return (
    <div className="flex items-center justify-between border-t border-[var(--border-subtle)] px-5 py-3">
      <span className="text-[12px] tabular-nums text-[var(--text-muted)]">
        Страница {page} из {pages} · всего {formatNumber(total)}
      </span>
      <div className="flex gap-1">
        <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => onChange(page - 1)}>
          <ChevronLeft size={14} />
          Назад
        </Button>
        <Button variant="ghost" size="sm" disabled={page >= pages} onClick={() => onChange(page + 1)}>
          Вперёд
          <ChevronRight size={14} />
        </Button>
      </div>
    </div>
  );
}

function ConversionsSkeleton() {
  return (
    <Card className="overflow-hidden" style={fade(360)}>
      <div className="space-y-3 p-5">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="flex items-center gap-4">
            <Skeleton className="h-9 w-9 rounded-[var(--radius-sm)]" />
            <Skeleton className="h-4 flex-1" />
            <Skeleton className="h-5 w-20" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-5 w-24" />
          </div>
        ))}
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Empty state
// ─────────────────────────────────────────────────────────────────────────

function ConversionsEmpty({ filtered, onReset }: { filtered: boolean; onReset: () => void }) {
  return (
    <Card className="overflow-hidden" style={fade(360)}>
      <div
        aria-hidden
        className="h-[2px] w-full"
        style={{
          background:
            'linear-gradient(90deg, transparent, color-mix(in oklch, var(--accent) 60%, transparent) 30%, color-mix(in oklch, var(--accent) 60%, transparent) 70%, transparent)',
        }}
      />
      <div className="flex flex-col items-center px-6 py-16 text-center">
        <div className="relative mb-5">
          <div
            aria-hidden
            className="absolute inset-0 rounded-full blur-2xl"
            style={{ background: 'radial-gradient(circle, color-mix(in oklch, var(--accent) 55%, transparent), transparent 70%)' }}
          />
          <div
            className="relative grid h-[72px] w-[72px] place-items-center rounded-full"
            style={{
              backgroundColor: 'color-mix(in oklch, var(--accent) 14%, var(--bg-raised))',
              border: '1px solid color-mix(in oklch, var(--accent) 32%, transparent)',
              color: 'var(--accent-bright)',
            }}
          >
            {filtered ? <FilterX size={28} strokeWidth={2} /> : <TrendingUp size={28} strokeWidth={2} />}
          </div>
        </div>
        <h3 className="text-[18px] font-semibold text-[var(--text)]">
          {filtered ? 'Под фильтр ничего не подходит' : 'Конверсий пока нет'}
        </h3>
        <p className="mt-2 max-w-[420px] text-[13px] leading-relaxed text-[var(--text-muted)]">
          {filtered
            ? 'Смягчите фильтры или расширьте период — и конверсии снова появятся в списке.'
            : 'Конверсии засчитываются автоматически по трафику с ваших трекинг-ссылок. Создайте ссылку, запустите трафик — и лиды с продажами появятся здесь.'}
        </p>
        <div className="mt-6">
          {filtered ? (
            <Button variant="secondary" size="md" onClick={onReset}>
              <X size={15} strokeWidth={2.5} />
              Сбросить фильтры
            </Button>
          ) : (
            <Button
              variant="gradient"
              size="md"
              onClick={() => {
                window.location.hash = '/links';
              }}
            >
              Перейти к ссылкам
              <ArrowRight size={15} strokeWidth={2.5} />
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Table
// ─────────────────────────────────────────────────────────────────────────

function ConversionsTable({
  items,
  page,
  pages,
  total,
  onPage,
}: {
  items: Conversion[];
  page: number;
  pages: number;
  total: number;
  onPage: (p: number) => void;
}) {
  return (
    <Card className="overflow-hidden" style={fade(360)}>
      <div
        aria-hidden
        className="h-[2px] w-full"
        style={{
          background:
            'linear-gradient(90deg, transparent, color-mix(in oklch, var(--accent) 60%, transparent) 30%, color-mix(in oklch, var(--accent) 60%, transparent) 70%, transparent)',
        }}
      />
      <div className="overflow-x-auto">
        <Table>
          <THead>
            <TR className="hover:bg-transparent">
              <TH>Дата</TH>
              <TH>Оффер</TH>
              <TH>Тип</TH>
              <TH>Гео</TH>
              <TH className="text-right">Сумма</TH>
              <TH className="text-right">Комиссия</TH>
              <TH>Статус</TH>
            </TR>
          </THead>
          <TBody>
            {items.map((c, i) => {
              const type = TYPE_META[c.type] ?? { label: c.type ?? '—', tone: 'neutral' as const };
              const status = STATUS_META[c.status] ?? {
                label: c.status ?? '—',
                tone: 'warning' as const,
              };
              const Icon = offerIcon(c.offerName ?? '');
              return (
                <TR key={c.id} style={rise(60 + Math.min(i, 14) * 30)}>
                  <TD className="whitespace-nowrap text-[12px] text-[var(--text-muted)] tabular-nums">
                    {formatDate(c.createdAt)}
                  </TD>
                  <TD>
                    <div className="flex items-center gap-2.5">
                      <span
                        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-sm)]"
                        style={{
                          backgroundColor: 'color-mix(in oklch, var(--accent) 13%, transparent)',
                          border: '1px solid color-mix(in oklch, var(--accent) 24%, transparent)',
                          color: 'var(--accent-bright)',
                        }}
                        aria-hidden
                      >
                        <Icon size={14} strokeWidth={2} />
                      </span>
                      <span className="font-medium text-[var(--text)]">{c.offerName ?? 'Без оффера'}</span>
                    </div>
                  </TD>
                  <TD>
                    <Badge tone={type.tone}>{type.label}</Badge>
                  </TD>
                  <TD>
                    <CountryTag code={c.country} />
                  </TD>
                  <TD className="text-right tabular-nums text-[var(--text-secondary)]">
                    {formatCents(c.amountCents)}
                  </TD>
                  <TD className="text-right tabular-nums">
                    <span
                      className="font-bold"
                      style={{
                        color: c.commissionCents > 0 ? 'var(--success)' : 'var(--text-quiet)',
                        letterSpacing: 'var(--tracking-tight)',
                      }}
                    >
                      {formatCents(c.commissionCents)}
                    </span>
                  </TD>
                  <TD>
                    <Badge tone={status.tone} dot={status.dot}>
                      {status.label}
                    </Badge>
                  </TD>
                </TR>
              );
            })}
          </TBody>
        </Table>
      </div>
      {pages > 1 && <Pagination page={page} pages={pages} total={total} onChange={onPage} />}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Page
// ─────────────────────────────────────────────────────────────────────────

export function ConversionsPage() {
  const [status, setStatus] = useState<ConversionStatus | ''>('');
  const [type, setType] = useState<ConversionType | ''>('');
  const [period, setPeriod] = useState<Period>('30d');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const q = partnerApi.conversions.useList({
    status: status || undefined,
    type: type || undefined,
    period,
    page,
    pageSize,
  });

  const allItems = q.data?.items ?? [];
  const items = search.trim()
    ? allItems.filter((c) => (c.offerName ?? '').toLowerCase().includes(search.trim().toLowerCase()))
    : allItems;
  const total = q.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const hasFilters = !!status || !!type || !!search.trim() || period !== '30d';
  const loading = q.isLoading;
  const isEmpty = !loading && items.length === 0;

  const approved = items.filter((c) => c.status === 'paid').length;
  const moderation = items.filter((c) => c.status === 'pending').length;
  const commission = items.reduce((s, c) => s + c.commissionCents, 0);

  const resetFilters = () => {
    setStatus('');
    setType('');
    setSearch('');
    setPeriod('30d');
    setPage(1);
  };

  return (
    <div className="relative mx-auto max-w-[1280px]">
      <style>{`
        @keyframes cvRise { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes cvFade { from { opacity: 0; } to { opacity: 1; } }
      `}</style>

      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-[-120px] -z-10 h-[360px] w-[760px] -translate-x-1/2"
        style={{
          background:
            'radial-gradient(ellipse at center, color-mix(in oklch, var(--accent) 20%, transparent), transparent 68%)',
          filter: 'blur(36px)',
        }}
      />

      <div style={rise(0)}>
        <PageHeader
          eyebrow="Партнёрская программа"
          title="Конверсии"
          subtitle="Все ваши лиды и продажи — с трафика по трекинг-ссылкам."
        />
      </div>

      {!loading && total > 0 && (
        <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatTile icon={TrendingUp} label="Всего" value={formatNumber(total)} tint="var(--accent-bright)" style={rise(70)} />
          <StatTile icon={CircleCheck} label="Одобрено" value={formatNumber(approved)} tint="var(--success)" style={rise(125)} />
          <StatTile icon={Clock} label="На модерации" value={formatNumber(moderation)} tint="var(--warning)" style={rise(180)} />
          <StatTile icon={Wallet} label="Комиссия" value={formatCents(commission)} tint="var(--success)" style={rise(235)} />
        </div>
      )}

      {/* Filters */}
      <div className="mb-5 flex flex-wrap items-center gap-2.5" style={rise(290)}>
        <div className="relative">
          <Search
            size={14}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-quiet)]"
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Поиск по офферу…"
            className="w-[240px] pl-9"
          />
        </div>
        <div className="w-[156px]">
          <Select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as ConversionStatus | '');
              setPage(1);
            }}
            aria-label="Статус"
          >
            <option value="">Все статусы</option>
            <option value="paid">Выплаченные</option>
            <option value="pending">Ожидают</option>
            <option value="declined">Отклонённые</option>
            <option value="refunded">Возвраты</option>
          </Select>
        </div>
        <div className="w-[148px]">
          <Select
            value={type}
            onChange={(e) => {
              setType(e.target.value as ConversionType | '');
              setPage(1);
            }}
            aria-label="Тип"
          >
            <option value="">Все типы</option>
            <option value="lead">Лиды</option>
            <option value="trial">Триалы</option>
            <option value="subscription">Подписки</option>
            <option value="purchase">Покупки</option>
            <option value="recurring">Рекуррент</option>
          </Select>
        </div>
        <Segmented<Period>
          value={period}
          onChange={(p) => {
            setPeriod(p);
            setPage(1);
          }}
          options={[
            { value: '7d', label: '7д' },
            { value: '30d', label: '30д' },
            { value: '90d', label: '90д' },
            { value: '12m', label: 'Год' },
          ]}
        />
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={resetFilters}>
            <X size={14} strokeWidth={2.5} />
            Сбросить
          </Button>
        )}
        <span className="ml-auto inline-flex items-center gap-1.5 text-[12px] tabular-nums text-[var(--text-quiet)]">
          <Coins size={13} className="text-[var(--text-muted)]" />
          {loading ? '—' : `Найдено: ${formatNumber(total)}`}
        </span>
      </div>

      {loading && <ConversionsSkeleton />}

      {isEmpty && <ConversionsEmpty filtered={hasFilters} onReset={resetFilters} />}

      {!loading && items.length > 0 && (
        <ConversionsTable items={items} page={page} pages={pages} total={total} onPage={setPage} />
      )}
    </div>
  );
}
